import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  Alert,
  Avatar,
  Button,
  Card,
  Checkbox,
  Empty,
  Form,
  Input,
  message,
  Modal,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';

const { Text } = Typography;
import {
  ApiOutlined,
  AppstoreOutlined,
  BellOutlined,
  MessageOutlined,
  CheckCircleOutlined,
  EditOutlined,
  KeyOutlined,
  LinkOutlined,
  RobotOutlined,
  SendOutlined,
  PlusOutlined,
  SearchOutlined,
  StopOutlined,
  TeamOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { AdminUsersImportModal } from './AdminUsersImportModal.tsx';
import { AdminUserDrawer } from './AdminUserDrawer.tsx';
import './AdminUserCard.css';
import { formatAgo, formatDateTime, presenceOf, PRESENCE_COLOR, PRESENCE_LABEL } from './userPresence.ts';
import { SpaceAvatar } from '../../shared/components/SpaceAvatar.tsx';
import type { AdminUser, AdminUserSpace, SpaceRole } from '@lens/shared';
import { PASSWORD_MIN_LENGTH } from '@lens/shared';
import { useAuth } from '../auth/AuthContext.tsx';
import { ApiError } from '../../shared/api/client.ts';
import { radius, roleBadge, shadows, spacing } from '../../shared/theme/tokens.ts';
import { useDocumentTitle } from '../../shared/hooks/useDocumentTitle.ts';
import {
  createAdminUser,
  getAdminUserSpaces,
  listAdminUsers,
  resetAdminUserPassword,
  setAdminUserSpaces,
  updateAdminUser,
} from './api.ts';

const SPACE_ROLE_LABELS: Record<SpaceRole, string> = {
  admin: 'Администратор',
  member: 'Участник',
  viewer: 'Наблюдатель',
};

const { Title } = Typography;

// ─── Create User Modal ──────────────────────────────────────────────────

interface CreateUserModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

interface CreateFormValues {
  login: string;
  displayName: string;
  email?: string;
  password: string;
  isPlatformAdmin: boolean;
}

function CreateUserModal({ open, onClose, onCreated }: CreateUserModalProps): JSX.Element {
  const [form] = Form.useForm<CreateFormValues>();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      form.resetFields();
      setError(null);
    }
  }, [open, form]);

  const handleOk = async (): Promise<void> => {
    let values: CreateFormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      await createAdminUser({
        login: values.login.trim(),
        displayName: values.displayName.trim(),
        email: values.email?.trim() || undefined,
        password: values.password,
        isPlatformAdmin: values.isPlatformAdmin ?? false,
      });
      void message.success('Пользователь создан');
      onCreated();
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'login_conflict') {
        form.setFields([{ name: 'login', errors: ['Пользователь с таким логином уже существует'] }]);
      } else if (err instanceof ApiError && err.code === 'password_policy_violation') {
        form.setFields([{ name: 'password', errors: [err.message] }]);
      } else if (err instanceof ApiError && err.code === 'password_equals_login') {
        form.setFields([{ name: 'password', errors: ['Пароль не должен совпадать с логином'] }]);
      } else {
        setError(err instanceof ApiError ? err.message : 'Не удалось создать пользователя');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title="Новый пользователь"
      open={open}
      onOk={() => void handleOk()}
      onCancel={onClose}
      okText="Создать"
      cancelText="Отмена"
      confirmLoading={submitting}
      destroyOnHidden
    >
      {error && <Alert type="error" message={error} showIcon style={{ marginBottom: 16 }} />}
      <Form<CreateFormValues>
        form={form}
        layout="vertical"
        requiredMark={false}
        disabled={submitting}
        autoComplete="off"
        initialValues={{ isPlatformAdmin: false }}
      >
        <Form.Item
          label="Логин"
          name="login"
          rules={[
            { required: true, message: 'Укажите логин' },
            { max: 64, message: 'Не более 64 символов' },
          ]}
        >
          <Input autoFocus autoComplete="off" data-testid="admin-user-login" />
        </Form.Item>
        <Form.Item
          label="Отображаемое имя"
          name="displayName"
          rules={[
            { required: true, message: 'Укажите имя' },
            { max: 120, message: 'Не более 120 символов' },
          ]}
        >
          <Input autoComplete="off" data-testid="admin-user-displayname" />
        </Form.Item>
        <Form.Item
          label="Email"
          name="email"
          rules={[
            { type: 'email', message: 'Некорректный email' },
            { max: 200, message: 'Не более 200 символов' },
          ]}
        >
          <Input autoComplete="off" data-testid="admin-user-email" />
        </Form.Item>
        <Form.Item
          label="Пароль"
          name="password"
          rules={[
            { required: true, message: 'Укажите пароль' },
            { min: PASSWORD_MIN_LENGTH, message: `Не менее ${PASSWORD_MIN_LENGTH} символов` },
            { max: 256, message: 'Не более 256 символов' },
          ]}
        >
          <Input.Password
            autoComplete="new-password"
            data-1p-ignore
            data-lpignore="true"
            data-form-type="other"
            data-testid="admin-user-password"
          />
        </Form.Item>
        <Form.Item name="isPlatformAdmin" valuePropName="checked">
          <Checkbox data-testid="admin-user-isadmin">Администратор платформы</Checkbox>
        </Form.Item>
      </Form>
    </Modal>
  );
}

// ─── Edit User Modal ────────────────────────────────────────────────────

interface EditUserModalProps {
  user: AdminUser | null;
  onClose: () => void;
  onUpdated: () => void;
}

interface EditFormValues {
  displayName: string;
  email?: string;
  isActive: boolean;
  isPlatformAdmin: boolean;
}

function EditUserModal({ user, onClose, onUpdated }: EditUserModalProps): JSX.Element {
  const [form] = Form.useForm<EditFormValues>();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      form.setFieldsValue({
        displayName: user.displayName,
        email: user.email ?? undefined,
        isActive: user.isActive,
        isPlatformAdmin: user.isPlatformAdmin,
      });
      setError(null);
    }
  }, [user, form]);

  const handleOk = async (): Promise<void> => {
    if (!user) return;
    let values: EditFormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      await updateAdminUser(user.id, {
        displayName: values.displayName.trim(),
        email: values.email?.trim() || null,
        isActive: values.isActive,
        isPlatformAdmin: values.isPlatformAdmin,
      });
      void message.success('Пользователь обновлён');
      onUpdated();
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'last_admin') {
        setError('Нельзя снять роль администратора или деактивировать последнего активного администратора');
      } else {
        setError(err instanceof ApiError ? err.message : 'Не удалось обновить пользователя');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={`Редактирование: ${user?.login ?? ''}`}
      open={!!user}
      onOk={() => void handleOk()}
      onCancel={onClose}
      okText="Сохранить"
      cancelText="Отмена"
      confirmLoading={submitting}
      destroyOnHidden
    >
      {error && <Alert type="error" message={error} showIcon style={{ marginBottom: 16 }} />}
      <Form<EditFormValues>
        form={form}
        layout="vertical"
        requiredMark={false}
        disabled={submitting}
        autoComplete="off"
      >
        <Form.Item
          label="Отображаемое имя"
          name="displayName"
          rules={[
            { required: true, message: 'Укажите имя' },
            { max: 120, message: 'Не более 120 символов' },
          ]}
        >
          <Input data-testid="admin-edit-displayname" />
        </Form.Item>
        <Form.Item
          label="Email"
          name="email"
          rules={[
            { type: 'email', message: 'Некорректный email' },
            { max: 200, message: 'Не более 200 символов' },
          ]}
        >
          <Input data-testid="admin-edit-email" />
        </Form.Item>
        <Form.Item name="isActive" valuePropName="checked">
          <Checkbox data-testid="admin-edit-isactive">Активен</Checkbox>
        </Form.Item>
        <Form.Item name="isPlatformAdmin" valuePropName="checked">
          <Checkbox data-testid="admin-edit-isadmin">Администратор платформы</Checkbox>
        </Form.Item>
      </Form>
    </Modal>
  );
}

// ─── Reset Password Modal ───────────────────────────────────────────────

interface ResetPasswordModalProps {
  user: AdminUser | null;
  onClose: () => void;
}

interface ResetPasswordFormValues {
  password: string;
}

function ResetPasswordModal({ user, onClose }: ResetPasswordModalProps): JSX.Element {
  const [form] = Form.useForm<ResetPasswordFormValues>();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      form.resetFields();
      setError(null);
    }
  }, [user, form]);

  const handleOk = async (): Promise<void> => {
    if (!user) return;
    let values: ResetPasswordFormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      await resetAdminUserPassword(user.id, { password: values.password });
      void message.success(`Пароль пользователя ${user.login} сброшен`);
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'password_policy_violation') {
        form.setFields([{ name: 'password', errors: [err.message] }]);
      } else if (err instanceof ApiError && err.code === 'password_equals_login') {
        form.setFields([{ name: 'password', errors: ['Пароль не должен совпадать с логином'] }]);
      } else {
        setError(err instanceof ApiError ? err.message : 'Не удалось сбросить пароль');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={`Сброс пароля: ${user?.login ?? ''}`}
      open={!!user}
      onOk={() => void handleOk()}
      onCancel={onClose}
      okText="Сбросить пароль"
      cancelText="Отмена"
      confirmLoading={submitting}
      destroyOnHidden
    >
      {error && <Alert type="error" message={error} showIcon style={{ marginBottom: 16 }} />}
      <Form<ResetPasswordFormValues>
        form={form}
        layout="vertical"
        requiredMark={false}
        disabled={submitting}
        autoComplete="off"
      >
        <Form.Item
          label="Новый пароль"
          name="password"
          rules={[
            { required: true, message: 'Укажите пароль' },
            { min: PASSWORD_MIN_LENGTH, message: `Не менее ${PASSWORD_MIN_LENGTH} символов` },
            { max: 256, message: 'Не более 256 символов' },
          ]}
        >
          <Input.Password
            autoFocus
            autoComplete="new-password"
            data-1p-ignore
            data-lpignore="true"
            data-form-type="other"
            data-testid="admin-reset-password"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// ─── Manage Spaces Modal ────────────────────────────────────────────────

interface ManageSpacesModalProps {
  user: AdminUser | null;
  onClose: () => void;
}

/** Локальное состояние строки матрицы: выдан ли доступ и с какой ролью. */
interface SpaceAccessRow extends AdminUserSpace {
  checked: boolean;
  draftRole: SpaceRole;
}

function ManageSpacesModal({ user, onClose }: ManageSpacesModalProps): JSX.Element {
  const [rows, setRows] = useState<SpaceAccessRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      setRows([]);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    getAdminUserSpaces(user.id)
      .then((data) => {
        if (cancelled) return;
        setRows(
          data.spaces.map((s) => ({
            ...s,
            checked: s.role !== null,
            draftRole: s.role ?? 'member',
          })),
        );
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : 'Не удалось загрузить пространства');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  const toggle = (spaceId: string, checked: boolean): void => {
    setRows((prev) => prev.map((r) => (r.spaceId === spaceId ? { ...r, checked } : r)));
  };

  const changeRole = (spaceId: string, role: SpaceRole): void => {
    setRows((prev) =>
      prev.map((r) => (r.spaceId === spaceId ? { ...r, checked: true, draftRole: role } : r)),
    );
  };

  const handleOk = async (): Promise<void> => {
    if (!user) return;
    setSubmitting(true);
    setError(null);
    try {
      const memberships = rows
        .filter((r) => r.checked)
        .map((r) => ({ spaceId: r.spaceId, role: r.draftRole }));
      const data = await setAdminUserSpaces(user.id, { memberships });
      setRows(
        data.spaces.map((s) => ({
          ...s,
          checked: s.role !== null,
          draftRole: s.role ?? 'member',
        })),
      );
      void message.success('Доступ к пространствам обновлён');
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'cannot_remove_last_admin') {
        setError(
          'Нельзя убрать доступ или понизить роль — пользователь остаётся последним администратором одного из пространств.',
        );
      } else {
        setError(err instanceof ApiError ? err.message : 'Не удалось сохранить доступ');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const selectedCount = rows.filter((r) => r.checked).length;

  return (
    <Modal
      title={`Доступ к пространствам: ${user?.login ?? ''}`}
      open={!!user}
      onOk={() => void handleOk()}
      onCancel={onClose}
      okText="Сохранить"
      cancelText="Отмена"
      confirmLoading={submitting}
      okButtonProps={{ disabled: loading }}
      width={560}
      destroyOnHidden
    >
      {error && <Alert type="error" message={error} showIcon style={{ marginBottom: 16 }} />}
      <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
        Отметьте пространства, к которым у пользователя есть доступ, и выберите роль в каждом.
        {selectedCount > 0 ? ` Выбрано: ${selectedCount}.` : ''}
      </Typography.Paragraph>
      {loading ? (
        <div style={{ textAlign: 'center', padding: spacing.lg }}>
          <Spin />
        </div>
      ) : rows.length === 0 ? (
        <Empty description="Нет пространств" />
      ) : (
        <div style={{ maxHeight: 420, overflowY: 'auto' }}>
          {rows.map((r) => (
            <div
              key={r.spaceId}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: spacing.md,
                padding: `${spacing.sm}px ${spacing.xs}px`,
                borderBottom: '1px solid var(--color-border-subtle, rgba(0,0,0,0.06))',
              }}
            >
              <Checkbox
                checked={r.checked}
                onChange={(e) => toggle(r.spaceId, e.target.checked)}
                data-testid={`admin-space-toggle-${r.slug}`}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: spacing.xs }}>
                  <SpaceAvatar name={r.name} icon={r.icon} seed={r.spaceId} size={22} />
                  {r.name}
                  {r.isArchived && (
                    <Tag style={{ marginLeft: spacing.xs }} color="default">
                      в архиве
                    </Tag>
                  )}
                </span>
              </Checkbox>
              <Select<SpaceRole>
                size="small"
                value={r.draftRole}
                disabled={!r.checked}
                onChange={(role) => changeRole(r.spaceId, role)}
                style={{ width: 160 }}
                data-testid={`admin-space-role-${r.slug}`}
                options={(Object.keys(SPACE_ROLE_LABELS) as SpaceRole[]).map((role) => ({
                  value: role,
                  label: SPACE_ROLE_LABELS[role],
                }))}
              />
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

// ─── Сводка и быстрые фильтры ───────────────────────────────────────────

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

type QuickFilter = 'all' | 'week' | 'never' | 'mcp' | 'telegram' | 'tgBot' | 'tgNotify';

const QUICK_FILTERS: Array<{
  key: QuickFilter;
  label: string;
  icon: ReactNode;
  color: string;
  match: (u: AdminUser) => boolean;
}> = [
  { key: 'all', label: 'Всего', icon: <TeamOutlined />, color: 'var(--color-primary)', match: () => true },
  {
    key: 'week',
    label: 'Заходили за неделю',
    icon: <CheckCircleOutlined />,
    color: 'var(--color-success)',
    match: (u) => !!u.lastSeenAt && Date.now() - new Date(u.lastSeenAt).getTime() < WEEK_MS,
  },
  {
    key: 'never',
    label: 'Ни разу не заходили',
    icon: <StopOutlined />,
    color: 'var(--color-warning)',
    match: (u) => !u.lastSeenAt,
  },
  {
    key: 'mcp',
    label: 'Настроили MCP',
    icon: <ApiOutlined />,
    color: 'var(--color-primary)',
    match: (u) => (u.mcpTokens ?? 0) > 0,
  },
  {
    key: 'telegram',
    label: 'Подключили Telegram',
    icon: <SendOutlined />,
    color: 'var(--color-info)',
    match: (u) => !!u.telegramLinked,
  },
  {
    key: 'tgBot',
    label: 'Нажали «Start» у бота',
    icon: <MessageOutlined />,
    color: 'var(--color-info)',
    match: (u) => !!u.telegramBotChatOpened,
  },
  {
    key: 'tgNotify',
    label: 'Получают уведомления',
    icon: <BellOutlined />,
    color: 'var(--color-success)',
    match: receivesTelegramNotify,
  },
];

/**
 * Получает личные уведомления в Telegram: аккаунт связан, «Start» у бота
 * нажат, сам человек их не выключил. Включены ли они в конкретном
 * пространстве, решает его админ — здесь это не учитывается.
 */
function receivesTelegramNotify(u: AdminUser): boolean {
  return !!u.telegramLinked && !!u.telegramBotChatOpened && u.telegramNotify !== 'off';
}

const NOTIFY_LABEL: Record<'on' | 'partial' | 'off', string> = {
  on: 'уведомления включены',
  partial: 'уведомления выключены в части пространств',
  off: 'уведомления выключены',
};

function computeStats(users: AdminUser[]): Record<QuickFilter, number> {
  const out = { all: 0, week: 0, never: 0, mcp: 0, telegram: 0, tgBot: 0, tgNotify: 0 } as Record<QuickFilter, number>;
  for (const f of QUICK_FILTERS) out[f.key] = users.filter(f.match).length;
  return out;
}

/** Четыре значка «что подключено»: MCP, Telegram, Jira, ключ LLM. */
function ConnectionIcons({ user }: { user: AdminUser }): JSX.Element {
  const items: Array<{ key: string; on: boolean; icon: ReactNode; title: string }> = [
    {
      key: 'mcp',
      on: (user.mcpTokens ?? 0) > 0,
      icon: <ApiOutlined />,
      title:
        (user.mcpTokens ?? 0) > 0
          ? `MCP: токенов ${user.mcpTokens}, использовался ${user.mcpLastUsedAt ? formatAgo(user.mcpLastUsedAt) : 'ни разу'}`
          : 'MCP не настроен',
    },
    {
      key: 'telegram',
      on: !!user.telegramLinked,
      icon: <SendOutlined />,
      title: user.telegramLinked
        ? [
            `Telegram: ${user.telegramUsername ? `@${user.telegramUsername}` : 'привязан'}`,
            user.telegramBotChatOpened ? '«Start» у бота нажат' : '«Start» у бота не нажат — бот не может писать',
            NOTIFY_LABEL[user.telegramNotify ?? 'on'],
          ].join(' · ')
        : 'Telegram не привязан',
    },
    {
      key: 'jira',
      on: !!user.jiraConnected,
      icon: <LinkOutlined />,
      title: user.jiraConnected ? 'Токен Jira задан' : 'Токен Jira не задан',
    },
    {
      key: 'llm',
      on: !!user.llmKeyConnected,
      icon: <RobotOutlined />,
      title: user.llmKeyConnected ? 'Ключ LLM задан' : 'Ключ LLM не задан',
    },
  ];
  return (
    <span className="au-conn-icons">
      {items.map((i) => (
        <Tooltip key={i.key} title={i.title}>
          <span
            className={`au-conn-icon${i.on ? ' au-conn-icon-on' : ''}`}
            data-testid={`admin-conn-${i.key}-${user.login}${i.on ? '-on' : ''}`}
          >
            {i.icon}
          </span>
        </Tooltip>
      ))}
    </span>
  );
}

// ─── Main Page ──────────────────────────────────────────────────────────

/** Для поиска: регистр и ё/е не важны. */
function normalizeForSearch(value: string): string {
  return value.toLowerCase().replace(/ё/g, 'е');
}

export function AdminUsersPage(): JSX.Element {
  useDocumentTitle('Пользователи');
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [resetPasswordUser, setResetPasswordUser] = useState<AdminUser | null>(null);
  const [spacesUser, setSpacesUser] = useState<AdminUser | null>(null);
  const [cardUser, setCardUser] = useState<AdminUser | null>(null);
  const [cardReload, setCardReload] = useState(0);
  const [quick, setQuick] = useState<QuickFilter>('all');
  const [search, setSearch] = useState('');

  const filteredUsers = useMemo(() => {
    const query = normalizeForSearch(search.trim());
    if (!query) return users;
    return users.filter((u) =>
      [u.login, u.displayName, u.email, u.telegramUsername].some(
        (v) => v && normalizeForSearch(v).includes(query),
      ),
    );
  }, [users, search]);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listAdminUsers();
      setUsers(data.users);
    } catch (err: any) {
      setError(err.message ?? 'Не удалось загрузить список пользователей');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadUsers();
  }, [loadUsers]);

  const afterUserChange = useCallback(() => {
    void loadUsers();
    setCardReload((n) => n + 1);
  }, [loadUsers]);

  const stats = useMemo(() => computeStats(users), [users]);
  const visibleUsers = useMemo(
    () => filteredUsers.filter(QUICK_FILTERS.find((f) => f.key === quick)!.match),
    [filteredUsers, quick],
  );

  if (!currentUser?.isPlatformAdmin) {
    return (
      <div style={{ width: '100%', padding: `${spacing.lg}px ${spacing.base}px` }}>
        <Alert type="error" message="Доступ запрещён. Эта страница доступна только администраторам платформы." />
      </div>
    );
  }

  const columns: ColumnsType<AdminUser> = [
    {
      title: 'Пользователь',
      key: 'user',
      sorter: (a, b) => a.displayName.localeCompare(b.displayName, 'ru'),
      render: (_: unknown, record: AdminUser) => {
        const presence = presenceOf(record.lastSeenAt);
        return (
          <div className="au-user">
            <span className="au-user-avatar">
              <Avatar size={32} src={record.avatarUrl ?? undefined} style={{ background: 'var(--color-primary)' }}>
                {(record.displayName || record.login).slice(0, 1).toUpperCase()}
              </Avatar>
              <Tooltip title={PRESENCE_LABEL[presence]}>
                <span className="au-user-dot" style={{ background: PRESENCE_COLOR[presence] }} />
              </Tooltip>
            </span>
            <span className="au-user-text">
              <span className="au-user-name">{record.displayName}</span>
              <span className="au-user-login">
                <span>{record.login}</span>
                {record.email ? ` · ${record.email}` : ''}
              </span>
            </span>
          </div>
        );
      },
    },
    {
      title: 'Последний визит',
      key: 'lastSeenAt',
      width: 170,
      defaultSortOrder: undefined,
      sorter: (a, b) => (a.lastSeenAt ?? '').localeCompare(b.lastSeenAt ?? ''),
      render: (_: unknown, record: AdminUser) =>
        record.lastSeenAt ? (
          <Tooltip title={formatDateTime(record.lastSeenAt)}>
            <span className="au-seen">
              <span>{formatAgo(record.lastSeenAt)}</span>
              <span className="au-seen-sub">
                вход {formatAgo(record.lastLoginAt)}
                {record.authMethod === 'sso' ? ' · SSO' : ''}
              </span>
            </span>
          </Tooltip>
        ) : (
          <Text type="secondary">ни разу</Text>
        ),
    },
    {
      title: 'Подключения',
      key: 'connections',
      width: 150,
      render: (_: unknown, record: AdminUser) => <ConnectionIcons user={record} />,
    },
    {
      title: 'Пространств',
      key: 'spaces',
      width: 120,
      align: 'right',
      sorter: (a, b) => (a.spacesCount ?? 0) - (b.spacesCount ?? 0),
      render: (_: unknown, record: AdminUser) =>
        record.spacesCount ? record.spacesCount : <Text type="secondary">—</Text>,
    },
    {
      title: 'Роль',
      key: 'role',
      width: 180,
      render: (_: unknown, record: AdminUser) =>
        record.isPlatformAdmin ? (
          <Tag
            style={{
              background: roleBadge.admin.bg,
              color: roleBadge.admin.color,
              border: 'none',
            }}
          >
            Администратор
          </Tag>
        ) : (
          <Tag
            style={{
              background: roleBadge.viewer.bg,
              color: roleBadge.viewer.color,
              border: 'none',
            }}
          >
            Пользователь
          </Tag>
        ),
      filters: [
        { text: 'Администратор', value: true },
        { text: 'Пользователь', value: false },
      ],
      onFilter: (value, record) => record.isPlatformAdmin === value,
    },
    {
      title: 'Статус',
      key: 'status',
      width: 140,
      render: (_: unknown, record: AdminUser) =>
        record.isActive ? (
          <Tag
            icon={<CheckCircleOutlined />}
            style={{
              background: 'var(--color-success-bg)',
              color: 'var(--color-success)',
              border: 'none',
            }}
          >
            Активен
          </Tag>
        ) : (
          <Tag
            icon={<StopOutlined />}
            style={{
              background: roleBadge.viewer.bg,
              color: 'var(--color-text-secondary)',
              border: 'none',
            }}
          >
            Неактивен
          </Tag>
        ),
      filters: [
        { text: 'Активен', value: true },
        { text: 'Неактивен', value: false },
      ],
      onFilter: (value, record) => record.isActive === value,
    },
    {
      title: '',
      key: 'actions',
      width: 120,
      render: (_: unknown, record: AdminUser) => (
        <Space size={0} onClick={(e) => e.stopPropagation()}>
          <Tooltip title="Изменить">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => setEditingUser(record)}
              data-testid={`admin-edit-${record.login}`}
            />
          </Tooltip>
          <Tooltip title="Доступ к пространствам">
            <Button
              type="text"
              size="small"
              icon={<AppstoreOutlined />}
              onClick={() => setSpacesUser(record)}
              data-testid={`admin-spaces-${record.login}`}
            />
          </Tooltip>
          <Tooltip title="Сбросить пароль">
            <Button
              type="text"
              size="small"
              icon={<KeyOutlined />}
              onClick={() => setResetPasswordUser(record)}
              data-testid={`admin-resetpw-${record.login}`}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ width: '100%', padding: `${spacing.lg}px ${spacing.base}px` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.lg }}>
        <Title
          level={1}
          style={{ margin: 0, fontSize: 22, display: 'flex', alignItems: 'center', gap: spacing.md }}
        >
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 40,
              height: 40,
              borderRadius: radius.md,
              background: 'var(--color-primary-bg)',
              color: 'var(--color-primary)',
              fontSize: 20,
            }}
          >
            <TeamOutlined />
          </span>
          Пользователи
        </Title>
        <Space>
          <Input
            allowClear
            autoFocus
            placeholder="Логин, имя, email или Telegram"
            prefix={<SearchOutlined />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 300 }}
            data-testid="admin-users-search"
          />
          <Button
            icon={<UploadOutlined />}
            onClick={() => setImportOpen(true)}
            data-testid="admin-import-users-btn"
          >
            Импорт из CSV / Excel
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setCreateOpen(true)}
            data-testid="admin-create-user-btn"
          >
            Новый пользователь
          </Button>
        </Space>
      </div>

      {error && <Alert type="error" message={error} style={{ marginBottom: spacing.base }} />}

      <div className="au-kpis">
        {QUICK_FILTERS.map((f) => {
          const value = stats[f.key];
          const share = stats.all > 0 ? Math.round((value / stats.all) * 100) : 0;
          return (
            <button
              key={f.key}
              type="button"
              className={`au-kpi ${quick === f.key ? 'au-kpi-active' : ''}`}
              onClick={() => setQuick(quick === f.key ? 'all' : f.key)}
              data-testid={`admin-users-kpi-${f.key}`}
            >
              <div className="au-kpi-label">
                {f.icon}
                {f.label}
              </div>
              <div className="au-kpi-value">
                {value}
                {f.key !== 'all' && <span className="au-kpi-share">{share}%</span>}
              </div>
              <div className="au-kpi-bar">
                <span style={{ width: `${f.key === 'all' ? 100 : share}%`, background: f.color }} />
              </div>
            </button>
          );
        })}
      </div>

      <Card
        variant="borderless"
        styles={{ body: { padding: spacing.xs } }}
        style={{ borderRadius: radius.lg, boxShadow: shadows.card }}
      >
        <Table<AdminUser>
          dataSource={visibleUsers}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={false}
          size="middle"
          locale={{ emptyText: search.trim() ? 'Никого не нашлось' : 'Нет пользователей' }}
          rowClassName={(record) => `au-row${!record.isActive ? ' ant-table-row-disabled' : ''}`}
          onRow={(record) => ({
            onClick: () => setCardUser(record),
            'data-testid': `admin-user-row-${record.login}`,
          } as React.HTMLAttributes<HTMLElement>)}
        />
      </Card>

      <CreateUserModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => void loadUsers()}
      />
      <EditUserModal
        user={editingUser}
        onClose={() => setEditingUser(null)}
        onUpdated={afterUserChange}
      />
      <ResetPasswordModal
        user={resetPasswordUser}
        onClose={() => setResetPasswordUser(null)}
      />
      <ManageSpacesModal
        user={spacesUser}
        onClose={() => {
          setSpacesUser(null);
          afterUserChange();
        }}
      />
      <AdminUserDrawer
        user={cardUser}
        reloadKey={cardReload}
        onClose={() => setCardUser(null)}
        onEdit={setEditingUser}
        onSpaces={setSpacesUser}
        onResetPassword={setResetPasswordUser}
      />
      <AdminUsersImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onDone={() => void loadUsers()}
      />
    </div>
  );
}
